/**
 * Tech Head Dashboard wire schemas. Drives the read-only review surface and
 * the status-transition workflow.
 *
 * The dashboard reads from `/api/admin/*`. Auth is the same device-token
 * scheme as the inspector PWA, but with `role: 'admin'` — see field.ts
 * DeviceTokenSchema. (M2 wires real role checks; M1 trusts the dev token.)
 *
 * Status-transition rules (M1):
 *   pending → approved        (tech head signs off; this is what unlocks
 *                              the autofill backend to serve the payload
 *                              to the extension)
 *   pending → sent-back        (tech head wants the inspector to re-do
 *                              something; attaches a structured note that
 *                              surfaces in the inspector's next sync)
 *   approved → sent-back       (allowed — caught a late issue)
 *   sent-back → pending        (inspector re-submits via the PWA)
 *
 * Status `failed` is reserved for the sync queue (e.g. validation failure
 * on submit). Tech head never transitions to/from it manually.
 */
import { z } from 'zod';
import { AccreditationCodeSchema } from './cer-portal.js';
import { InspectorIdSchema } from './field.js';
import { JobStatusSchema } from './jobs.js';
/**
 * A report's QA outcome — and ONLY its QA outcome.
 *
 * The report used to carry lifecycle states too (`uploaded`, `sent`,
 * `failed`, `sent-back`), which duplicated the job's lifecycle and then drifted
 * from it: BTY7033068 sat at `pending` while its job had gone through
 * uploaded and closed, so the ten-day clock counted down against work already
 * delivered. Two records describing one journey will always disagree
 * eventually; the fix is for only one of them to describe it.
 *
 * The JOB owns the lifecycle — booked, in field, submitted, QA passed, ready
 * to submit, Submitted to CER, complete (see JobStatusSchema and the label
 * map in docs/GLOSSARY.md). The report answers one narrower question: what did
 * QA decide about this revision?
 *
 * Stored on the DDB item as `status`, which is the `by-status` GSI's partition
 * key — the attribute name is load-bearing for the queue's read path, so the
 * NAME stays while the MEANING narrows.
 */
export const AdminReportStatusSchema = z.enum([
    /** Arrived, not yet reviewed. */
    'pending',
    /** QA passed. Whether it has reached CER is the JOB's business. */
    'approved',
    /** Returned to the inspector for detail. */
    'information-required',
    /** Returned to the inspector for another visit. */
    'revisit-required',
]);
/**
 * One row in the dashboard's job list. Carries enough to render the row +
 * decide whether to drill in, without pulling the full FieldReport.
 */
/** One revision of a report, for the list view's version affordance. */
export const ReportRevisionMetaSchema = z.object({
    submittedAt: z.string(),
    status: AdminReportStatusSchema,
});
export const AdminReportSummarySchema = z.object({
    jobId: AccreditationCodeSchema,
    /** The LATEST revision. Older ones are listed in `versions`. */
    submittedAt: z.string(),
    inspectorId: InspectorIdSchema,
    status: AdminReportStatusSchema,
    portalApprovedAt: z.string().optional(),
    ackedAt: z.string().optional(),
    /** Total photos captured. */
    hardwareConflictCount: z.number().int().nonnegative().optional(),
    photoCount: z.number().int().nonnegative(),
    /** Count of clause responses by outcome — drives the row's status pills. */
    clauseCounts: z.object({
        compliant: z.number().int().nonnegative(),
        nonCompliant: z.number().int().nonnegative(),
        notApplicable: z.number().int().nonnegative(),
    }),
    /** Homeowner name from the consent record — quick row identifier. */
    homeownerName: z.string().optional(),
    /**
     * Every revision of this report, newest first, including the one this row
     * represents. A supervisor edit stores a fresh revision rather than mutating
     * the inspector's original, so a corrected report has more than one — and the
     * queue must still show it as ONE report, not one line per correction.
     *
     * Length 1 for the ordinary case. Present on every row so the UI never has to
     * ask a second question to know whether a version picker is warranted.
     */
    versions: z.array(ReportRevisionMetaSchema).min(1),
    /**
     * How many of the field tool's own checks were still unmet at submit — the
     * length of the submission's `outstandingAtSubmit` (see field.ts). Kept
     * for stats. The queue badge reads `outstandingNowCount` instead.
     *
     * The count rather than the list: a bad submission can carry dozens of
     * issue strings and this endpoint returns up to 200 rows. The detail view
     * loads the whole FieldReport and reads the list itself.
     *
     * Absent for pre-Aug-2026 submissions, which recorded nothing and were
     * complete by definition. `fieldReadiness()` in field-readiness.ts is the
     * one place that distinguishes that from a recorded zero.
     */
    outstandingAtSubmitCount: z.number().int().nonnegative().optional(),
    /**
     * How many of those submit-time lines are still open, given the photos on
     * the report now. Photo lines clear once that shot is present; other lines
     * stay. This is the queue's needs-work badge.
     *
     * Absent on rows stored before the live count existed. The list computes
     * it on read in that case, and a photo add, delete, or re-file persists it.
     * A missing live count with a present `outstandingAtSubmitCount` is that
     * backlog — not a claim that nothing is outstanding.
     */
    outstandingNowCount: z.number().int().nonnegative().optional(),
    /**
     * The inspection date (`YYYY-MM-DD`) — the day the inspector was on site,
     * and the day the 10-day upload clock starts. Absent on rows whose denormalized summary predates this
     * field, and on reports with no recorded inspection date at all — the
     * queue shows those as "No inspection date" rather than inventing one.
     */
    inspectionDate: z.string().optional(),
    /** Single-line site address, for free-text search in the queue. */
    siteAddress: z.string().optional(),
    /**
     * The JOB's lifecycle status, joined at read time rather than stored on
     * the row: it changes independently of the report, so a denormalized copy
     * would go stale exactly when it matters.
     *
     * Needed because "has this reached CER?" is a job-level fact. The office
     * marks the job uploaded — sometimes after pushing the report through the
     * legacy portal path, with the report itself never leaving `pending`. The
     * upload clock must read that, or it counts down against work already
     * delivered.
     */
    jobStatus: JobStatusSchema.optional(),
});
/**
 * Filter inputs accepted by GET /api/admin/submissions.
 *
 * `limit` uses `z.coerce.number()` because query-string values arrive as
 * strings — Fastify's parser leaves them that way and we need the coercion
 * to land at runtime without forcing every caller to JSON.parse first.
 */
export const AdminReportFilterSchema = z.object({
    inspectorId: InspectorIdSchema.optional(),
    status: AdminReportStatusSchema.optional(),
    /** Inclusive ISO date floor (YYYY-MM-DD). */
    fromDate: z.string().optional(),
    /** Inclusive ISO date ceiling (YYYY-MM-DD). */
    toDate: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
/**
 * Status-transition request body. The `note` is mandatory when transitioning
 * to `sent-back` (the inspector needs to know what to fix) and optional
 * everywhere else.
 */
export const AdminStatusTransitionSchema = z
    .object({
    toStatus: z.enum(['approved', 'information-required', 'revisit-required', 'pending']),
    note: z.string().optional(),
})
    .superRefine((data, ctx) => {
    if (['information-required', 'revisit-required'].includes(data.toStatus) &&
        !data.note?.trim()) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'note is required when returning a report to the inspector',
            path: ['note'],
        });
    }
});
/**
 * One audit-trail entry attached to a submission. The dashboard renders the
 * full trail under each job's detail view.
 */
export const AdminAuditEntrySchema = z.object({
    at: z.string(),
    actor: z.string(),
    fromStatus: AdminReportStatusSchema,
    toStatus: AdminReportStatusSchema,
    note: z.string().optional(),
});
//# sourceMappingURL=admin.js.map